import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../../utils/joiConfig';
import {
  Input,
  Text,
  Select,
  Icon,
  Link,
  FormRow,
  Stack,
  FileDropField,
  FileList
} from '@efast_public/fjd-component-library';
import { Payment } from '../../api/govos.types';
import { EPayBLFormProps, PaymentSubFormProps } from './Payment.types';
import { usePaymentForm } from './usePaymentForm';
import { BezahldienstPolicy } from '../../api/models/Bezahldienst/BezahldienstDto';

const getDefaultValues = (
  data?: Payment,
  serviceId?: number
): EPayBLFormProps => ({
  serviceId: serviceId || 0,
  policy: data?.policy || 'OPTIONAL',
  mandantnr: data?.paymentService?.params?.mandantnr || '',
  bewirtschafternr: data?.paymentService?.params?.bewirtschafternr || '',
  kennzeichenMahnverfahren:
    data?.paymentService?.params?.kennzeichenMahnverfahren || '',
  objektNr: data?.paymentService?.params?.objektNr || '',
  bookingDescription: data?.paymentService?.params?.bookingDescription || '',
  clientcert: undefined,
  clientcertpassword: '',
  faelligkeitintagen: '0',
  haushaltsStelle: '',
  waehrungskennzeichen: 'EUR',
  transaktionsspr: 'de'
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>
) =>
  CustomJoi.object<EPayBLFormProps>({
    serviceId: CustomJoi.number().required(),
    policy: CustomJoi.string().required(),
    mandantnr: CustomJoi.string().required(),
    bewirtschafternr: CustomJoi.string().required(),
    kennzeichenMahnverfahren: CustomJoi.string().required(),
    objektNr: CustomJoi.string().optional().allow(''),
    bookingDescription: CustomJoi.string()
      .required()
      .pattern(/^[A-Z0-9 .,\-/+*$%&ÄÖÜAOU]+$/)
      .max(27),
    clientcert: CustomJoi.any().optional(),
    clientcertpassword: CustomJoi.string().optional().allow(''),
    faelligkeitintagen: CustomJoi.string().required(),
    haushaltsStelle: CustomJoi.string().required(),
    waehrungskennzeichen: CustomJoi.string().required(),
    transaktionsspr: CustomJoi.string().required()
  });

export function EPayBLForm({
  paymentServices,
  payment,
  parentState,
  ...props
}: PaymentSubFormProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const CustomJoi = createJoiWithTranslations(t);

  const { register, formState, handleSubmit, setValue, watch } =
    useForm<EPayBLFormProps>({
      defaultValues: getDefaultValues(
        payment,
        parentState.selectedService.serviceId
      ),
      resolver: joiResolver(getValidationSchema(CustomJoi)),
      mode: 'onBlur'
    });

  usePaymentForm({
    paymentServices,
    payment,
    handleSubmit,
    parentState,
    ...props
  });

  const config = watch();

  const policyOptions = [
    {
      value: 'mandatory',
      label: t('EditPaymentConfigModal.policyOptions.mandatory')
    },
    {
      value: 'optional',
      label: t('EditPaymentConfigModal.policyOptions.optional')
    }
  ];

  return (
    <Stack spacing="xs">
      {/* Policy Selection */}
      <FormRow
        label={t('EPayBLForm.fields.policy')}
        tooltip={t('EPayBLForm.fields.policyHint')}
        validationMessage={
          formState.errors.policy?.message
            ? t(formState.errors.policy.message)
            : undefined
        }
        variant={formState.errors.policy ? 'error' : undefined}
      >
        <Select
          value={config.policy}
          onChange={(value) =>
            setValue(
              'policy',
              (value.at(0)?.value || 'INACTIVE') as BezahldienstPolicy,
              {
                shouldValidate: true,
                shouldDirty: true
              }
            )
          }
          options={policyOptions}
          placeholder={t('select.placeholder')}
          style={{ width: '100%' }}
          defaultOptions={policyOptions.filter(
            (o) => o.value === config.policy
          )}
        />
      </FormRow>

      {/* Mandantennummer */}
      <FormRow
        label={t('EPayBLForm.fields.mandantnr')}
        validationMessage={
          formState.errors.mandantnr?.message
            ? t(formState.errors.mandantnr.message)
            : undefined
        }
        variant={formState.errors.mandantnr ? 'error' : undefined}
      >
        <Input
          {...register('mandantnr')}
          placeholder="1122334455"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Bewirtschafternummer */}
      <FormRow
        label={t('EPayBLForm.fields.bewirtschafternr')}
        validationMessage={
          formState.errors.bewirtschafternr?.message
            ? t(formState.errors.bewirtschafternr.message)
            : undefined
        }
        variant={formState.errors.bewirtschafternr ? 'error' : undefined}
      >
        <Input
          {...register('bewirtschafternr')}
          placeholder="123654"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Haushaltsstelle */}
      <FormRow
        label={t('EPayBLForm.fields.haushaltsStelle')}
        tooltip={t('EPayBLForm.fields.haushaltsstelleHint')}
        validationMessage={
          formState.errors.haushaltsStelle?.message
            ? t(formState.errors.haushaltsStelle.message)
            : undefined
        }
        variant={formState.errors.haushaltsStelle ? 'error' : undefined}
      >
        <Input
          {...register('haushaltsStelle')}
          placeholder="HST0001"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Kennzeichen Mahnverfahren */}
      <FormRow
        label={t('EPayBLForm.fields.kennzeichenMahnverfahren')}
        tooltip={t('EPayBLForm.fields.kennzeichenMahnverfahrenHint')}
        validationMessage={
          formState.errors.kennzeichenMahnverfahren?.message
            ? t(formState.errors.kennzeichenMahnverfahren.message)
            : undefined
        }
        variant={
          formState.errors.kennzeichenMahnverfahren ? 'error' : undefined
        }
      >
        <Input
          {...register('kennzeichenMahnverfahren')}
          placeholder=""
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Objekt-Nr. */}
      <FormRow
        label={t('EPayBLForm.fields.objektNr')}
        tooltip={t('EPayBLForm.fields.objektNrHint')}
        validationMessage={
          formState.errors.objektNr?.message
            ? t(formState.errors.objektNr.message)
            : undefined
        }
        variant={formState.errors.objektNr ? 'error' : undefined}
      >
        <Input
          {...register('objektNr')}
          placeholder=""
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Beschreibung der Buchung */}
      <FormRow
        label={t('EPayBLForm.fields.bookingDescription')}
        tooltip={t('EPayBLForm.fields.bookingDescriptionHint')}
        validationMessage={
          formState.errors.bookingDescription?.message
            ? t(formState.errors.bookingDescription.message)
            : undefined
        }
        variant={formState.errors.bookingDescription ? 'error' : undefined}
      >
        <Input
          {...register('bookingDescription')}
          placeholder="KFZ-Abmeldung $(az)"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Certificate Notice */}
      <div
        style={{
          padding: '12px',
          backgroundColor: '#E3F2FD',
          borderLeft: '4px solid #2196F3',
          borderRadius: '4px'
        }}
      >
        <Text size="s">
          {t('EPayBLForm.certificateNotice')}
          <Icon
            name="information-outline"
            size="sm"
            style={{ marginLeft: '4px' }}
          />
        </Text>
      </div>

      {/* Client-Zertifikat (optional) */}
      <FormRow
        label={t('EPayBLForm.fields.clientcert')}
        validationMessage={
          formState.errors.clientcert?.message
            ? t(formState.errors.clientcert.message)
            : undefined
        }
        variant={formState.errors.clientcert ? 'error' : undefined}
      >
        <Stack spacing="xs">
          <FileDropField
            label="Upload files"
            onAdd={(files) => {
              const file = files[0] || null;
              setUploadedFile(file);
              setValue('clientcert', file, { shouldDirty: true });
            }}
          />
          <FileList
            deleteLabel="Löschen"
            files={uploadedFile ? [uploadedFile] : []}
            onDelete={() => {
              setUploadedFile(null);
              setValue('clientcert', undefined, { shouldDirty: true });
            }}
          />
        </Stack>
      </FormRow>

      {/* Zertifikatspasswort */}
      <FormRow
        label={t('EPayBLForm.fields.clientcertpassword')}
        tooltip={t('EPayBLForm.fields.clientcertpasswordHint')}
        validationMessage={
          formState.errors.clientcertpassword?.message
            ? t(formState.errors.clientcertpassword.message)
            : undefined
        }
        variant={formState.errors.clientcertpassword ? 'error' : undefined}
      >
        <div style={{ position: 'relative' }}>
          <Input
            {...register('clientcertpassword')}
            type={showPassword ? 'text' : 'password'}
            placeholder=""
            style={{ width: '100%', paddingRight: '120px' }}
          />
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowPassword(!showPassword);
            }}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Icon name={showPassword ? 'data-vis-1' : 'data-vis-2'} size="sm" />
            <Text size="s">
              {showPassword
                ? t('EditPaymentConfigModal.actions.hidePassword')
                : t('EditPaymentConfigModal.actions.showPassword')}
            </Text>
          </Link>
        </div>
      </FormRow>

      {/* Fälligkeit in Tagen */}
      <FormRow
        label={t('EPayBLForm.fields.faelligkeitintagen')}
        tooltip={t('EPayBLForm.fields.faelligkeitintagenHint')}
        validationMessage={
          formState.errors.faelligkeitintagen?.message
            ? t(formState.errors.faelligkeitintagen.message)
            : undefined
        }
        variant={formState.errors.faelligkeitintagen ? 'error' : undefined}
      >
        <Input
          {...register('faelligkeitintagen')}
          placeholder="0"
          type="number"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Währungskennzeichen */}
      <FormRow
        label={t('EPayBLForm.fields.waehrungskennzeichen')}
        tooltip={t('EPayBLForm.fields.waehrungskennzeichenHint')}
        validationMessage={
          formState.errors.waehrungskennzeichen?.message
            ? t(formState.errors.waehrungskennzeichen.message)
            : undefined
        }
        variant={formState.errors.waehrungskennzeichen ? 'error' : undefined}
      >
        <Input
          {...register('waehrungskennzeichen')}
          placeholder="EUR"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Transaktionssprache */}
      <FormRow
        label={t('EPayBLForm.fields.transaktionsspr')}
        tooltip={t('EPayBLForm.fields.transaktionssprHint')}
        validationMessage={
          formState.errors.transaktionsspr?.message
            ? t(formState.errors.transaktionsspr.message)
            : undefined
        }
        variant={formState.errors.transaktionsspr ? 'error' : undefined}
      >
        <Input
          {...register('transaktionsspr')}
          placeholder="de"
          style={{ width: '100%' }}
        />
      </FormRow>
    </Stack>
  );
}
